// Leihinventar mit Kalender (docs/veranstaltungen/SPEZIFIKATION.md §9, M5): Monatskalender je Leihartikel mit Balken je Buchung,
// Veranstaltung und Zuleihe; bei der Anfrage „Bei Partner zuleihen“ und „Menge kürzen“; „Leihbestand festlegen“ je Standort ohne
// Wareneingang (Korrekturbuchung). Stück in Tausendstel (10 Garnituren = 10000), Tage 'YYYY-MM-DD', Geld in Cent.
import { defineContract } from '@shared/contract'
import { zId } from '@shared/ids'
import { z } from 'zod'
import { zDay, zNote, zUnitQty, zUnitQtyOrZero } from './common'
import { zEventView } from './event'
import { RENTAL_STATUSES } from './rental'

const zPieces = zUnitQty.multipleOf(1000, { error: 'Leihartikel werden in ganzen Stücken gezählt.' })
const zMonth = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, { error: 'Bitte einen Monat im Format JJJJ-MM angeben.' })

// ---- Zuleihe und Kürzen (Veranstaltung) --------------------------------------------------------------------------------

export const eventAddLoan = defineContract({
  name: 'sales.event.addLoan',
  title: 'Leihartikel bei einem Partner zuleihen',
  description: 'Vermerkt eine Zuleihe für die Veranstaltung – sie erhöht die Verfügbarkeit nur für diese Veranstaltung.',
  llm: {
    description:
      'Legt für eine Veranstaltung (Anfrage oder Angebot) eine Zuleihe an: Leihartikel, Partner (Lieferant oder Kollege), Stück in ' +
      'Tausendstel, Zeitraum (ohne Angabe Anlieferung bis Abholung), Kosten in Cent (netto, nur Vermerk) und Notiz. Die Zuleihe zählt ' +
      'nur für diese Veranstaltung zur Verfügbarkeit (Ampel); bei der Zusage wird nur der eigene Teil gebucht. Zurücknehmen mit ' +
      'sales.event.cancelLoan.',
    keywords: ['zuleihen', 'Partner leihen', 'Fremdmiete', 'Garnituren dazu', 'nicht verfügbar'],
  },
  input: z.strictObject({
    id: zId,
    itemId: zId,
    partnerId: zId,
    qty: zPieces,
    fromDate: zDay.optional(),
    toDate: zDay.optional(),
    costCents: z.int({ error: 'Bitte einen Betrag eingeben.' }).min(0, { error: 'Die Kosten dürfen nicht negativ sein.' }).max(100_000_000).nullish(),
    note: zNote,
  }),
  output: zEventView,
  permission: 'sales.event.write',
  feature: 'events',
  risk: 'write',
  scope: 'record',
  effects: { reversible: true },
})

export const eventCancelLoan = defineContract({
  name: 'sales.event.cancelLoan',
  title: 'Zuleihe zurücknehmen',
  description: 'Nimmt eine Zuleihe zurück – die Verfügbarkeit der Veranstaltung sinkt wieder.',
  llm: {
    description: 'Nimmt eine Zuleihe einer Veranstaltung (Anfrage oder Angebot) zurück; sie bleibt im Verlauf sichtbar.',
    keywords: ['Zuleihe zurücknehmen', 'Fremdmiete absagen'],
  },
  input: z.strictObject({ id: zId, loanId: zId }),
  output: zEventView,
  permission: 'sales.event.write',
  feature: 'events',
  risk: 'write',
  scope: 'record',
})

export const eventShortenRental = defineContract({
  name: 'sales.event.shortenRental',
  title: 'Menge eines Leihartikels kürzen',
  description: 'Setzt die gewünschte Menge eines Leihartikels der Anfrage herab – auch im Angebot, falls es schon eines gibt.',
  llm: {
    description:
      'Kürzt bei einer Veranstaltung (Anfrage oder Angebot) die Menge eines Leihartikels auf `qty` (Stück × 1000; 0 entfernt ihn). ' +
      'Steht der Leihartikel schon in einem Angebot, wird dessen Leihgebühr mitgeändert (beim abgegebenen Angebot als neue Fassung). ' +
      'Typisch, wenn die Ampel „nicht verfügbar“ zeigt und nicht zugeliehen werden soll.',
    keywords: ['Menge kürzen', 'weniger Garnituren', 'nicht verfügbar', 'reduzieren'],
  },
  input: z.strictObject({ id: zId, itemId: zId, qty: zUnitQtyOrZero.multipleOf(1000, { error: 'Leihartikel werden in ganzen Stücken gezählt.' }) }),
  output: zEventView,
  permission: 'sales.event.write',
  feature: 'events',
  risk: 'write',
  scope: 'record',
})

// ---- Kalender und Leihbestand (Verleih) --------------------------------------------------------------------------------

export const RENTAL_BAR_KINDS = ['booking', 'event', 'loan'] as const

export const zRentalBar = z.object({
  /** booking = Leihbuchung, event = Buchungen einer Veranstaltung (zusammengefasst), loan = Zuleihe von einem Partner */
  kind: z.enum(RENTAL_BAR_KINDS),
  /** Leihbuchung bzw. Zuleihe; bei `event` die erste Buchung */
  id: zId,
  eventId: zId.nullable(),
  /** „VA-2026-00012 Sommerfest“, „Müller GmbH“, „Zuleihe: Partner …“ */
  label: z.string(),
  customerName: z.string().nullable(),
  fromDate: z.string(),
  toDate: z.string(),
  qty: z.int(),
  status: z.enum(RENTAL_STATUSES).nullable(),
  statusLabel: z.string(),
})

export const rentalCalendar = defineContract({
  name: 'sales.rental.calendar',
  title: 'Leihkalender eines Monats anzeigen',
  description: 'Je Leihartikel und Tag eines Monats Bestand, gebunden und frei – dazu Balken je Buchung, Veranstaltung und Zuleihe.',
  llm: {
    description:
      'Liefert für einen Monat (`month` JJJJ-MM) je Leihartikel den Bestand am Heimatstandort (bzw. allen Lagern und Märkten), je Tag ' +
      'gebundene und freie Stücke (Tausendstel) und die Balken: Leihbuchungen, Veranstaltungen (ihre Buchungen zusammengefasst) und ' +
      'Zuleihen von Partnern. Verwenden für „Wie voll sind die Garnituren im Juni?“. Einzelne Tage: sales.rental.availability.',
    keywords: ['Leihkalender', 'Monatskalender', 'Belegung', 'Verleih', 'Garnituren Juni'],
  },
  input: z.strictObject({ month: zMonth, itemId: zId.optional(), locationId: zId.optional() }),
  output: z.object({
    month: z.string(),
    fromDate: z.string(),
    toDate: z.string(),
    items: z.array(
      z.object({
        itemId: zId,
        sku: z.string(),
        name: z.string(),
        unit: z.string(),
        serialTracked: z.boolean(),
        /** Bestand (zu Hause ohne Sperrbestand + beim Kunden), Tausendstel */
        totalQty: z.int(),
        /** je Standort (zu Hause, ohne Sperrbestand) – für „Leihbestand festlegen“ */
        locations: z.array(z.object({ locationId: zId, code: z.string(), name: z.string(), onHandQty: z.int(), blockedQty: z.int() })),
        days: z.array(z.object({ date: z.string(), bookedQty: z.int(), freeQty: z.int(), loanQty: z.int() })),
        bars: z.array(zRentalBar),
      }),
    ),
  }),
  permission: 'sales.rental.read',
  feature: 'rental',
  risk: 'read',
  scope: 'any',
})

export const rentalSetStock = defineContract({
  name: 'sales.rental.setStock',
  title: 'Leihbestand festlegen',
  description: 'Setzt den Bestand eines Leihartikels an einem Standort ohne Wareneingang – als Korrekturbuchung über die Differenz.',
  llm: {
    description:
      'Legt fest, wie viele Stück eines Leihartikels (ohne Seriennummern) an einem Lager oder Markt zu Hause sind (`qty` Tausendstel, ' +
      'ohne Sperrbestand und ohne Ausgegebenes). Gebucht wird die Differenz als Korrekturbuchung (Grund „Korrektur einer Fehlbuchung“) ' +
      'über inventory.adjustment.book – dafür braucht es auch das Recht für Korrekturbuchungen; über der Grenze wartet sie auf Freigabe. ' +
      'Funktioniert unabhängig davon, ob der Betrieb die Bestandsführung sonst nutzt.',
    keywords: ['Leihbestand', 'Bestand festlegen', 'Garnituren anlegen', 'Inventar Verleih', 'Anfangsbestand'],
  },
  input: z.strictObject({ itemId: zId, locationId: zId, qty: zUnitQtyOrZero.multipleOf(1000, { error: 'Leihartikel werden in ganzen Stücken gezählt.' }), note: zNote }),
  output: z.object({
    itemId: zId,
    locationId: zId,
    beforeQty: z.int(),
    afterQty: z.int(),
    /** posted = gebucht · pending = wartet auf Freigabe · unchanged = stimmte schon */
    outcome: z.enum(['posted', 'pending', 'unchanged']),
    documentId: zId.nullable(),
    documentNumber: z.string().nullable(),
    message: z.string(),
  }),
  permission: 'sales.rental.write',
  feature: 'rental',
  risk: 'write',
  scope: (input) => [input.locationId],
  effects: { reversible: false },
})
